/**
 * FloatingInstallPWA — Pastille flottante discrète en bas à gauche du site public,
 * proposant d'installer l'application sur mobile / tablette / desktop.
 *
 * - Toujours visible (FAB en bas-gauche) tant que l'app n'est pas installée.
 * - Capture `beforeinstallprompt` (Chrome/Edge/Android) -> ouvre la prompt native.
 * - Sur iOS Safari : affiche un mini-tooltip avec instructions « Partager → Sur l'écran d'accueil ».
 * - Sur autres navigateurs (Firefox, Safari desktop, etc.) : affiche les instructions manuelles
 *   du menu navigateur.
 * - Masquée si l'app tourne déjà en mode standalone.
 * - Bouton « Fermer » qui mémorise le choix 14 jours via localStorage.
 */
import React, { useEffect, useState } from "react";
import { Download, X, Smartphone } from "lucide-react";
import { useSettings } from "../../lib/useSettings";
import { toast } from "sonner";

const DISMISS_KEY = "iphb_pwa_install_dismissed_until";
const DISMISS_DAYS = 14;

function detectBrowser() {
  if (typeof navigator === "undefined") return "unknown";
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/i.test(ua) && !/CriOS|FxiOS|EdgiOS/i.test(ua)) return "ios";
  if (/Android/i.test(ua)) return "android";
  if (/EdgiOS|Edg\//i.test(ua)) return "edge";
  if (/CriOS|Chrome\//i.test(ua) && !/Edg\//i.test(ua)) return "chrome";
  if (/FxiOS|Firefox/i.test(ua)) return "firefox";
  if (/Safari\//i.test(ua) && !/Chrome\//i.test(ua)) return "safari-desktop";
  return "other";
}

export default function FloatingInstallPWA() {
  const { settings } = useSettings();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [browser] = useState(() => detectBrowser());

  const isIOS = browser === "ios";

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Mode standalone -> déjà installée
    if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) {
      setInstalled(true);
      return;
    }
    if (window.navigator.standalone === true) {
      setInstalled(true);
      return;
    }
    // Dismiss persisté ?
    try {
      const until = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
      if (until && until > Date.now()) {
        setDismissed(true);
      }
    } catch { /* localStorage indispo */ }

    const onBIP = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
      setOpen(false);
      toast.success("Application installée sur votre appareil");
    };
    window.addEventListener("beforeinstallprompt", onBIP);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBIP);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => {
    setOpen(false);
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000));
    } catch { /* noop */ }
    setDismissed(true);
  };

  const install = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome !== "accepted") {
        toast("Installation annulée");
      }
      setDeferredPrompt(null);
      setOpen(false);
      return;
    }
    // Pas de BIP dispo → on garde le panneau ouvert avec instructions manuelles.
    // (rien à faire — le panneau d'instructions est déjà visible)
  };

  // Masqué uniquement si déjà installée ou refus persistant (14j)
  if (installed || dismissed) return null;

  const clubName = settings?.company_name || "Istres Provence Handball";

  // Instructions adaptées au navigateur quand pas de BIP
  const browserInstructions = (() => {
    if (browser === "ios") {
      return {
        title: "Sur iPhone / iPad",
        steps: [
          <>Touchez l'icône <span className="font-bold">Partager</span> en bas de Safari.</>,
          <>Choisissez <span className="font-bold">« Sur l'écran d'accueil »</span>.</>,
          <>Validez avec <span className="font-bold">Ajouter</span>.</>,
        ],
      };
    }
    if (browser === "firefox") {
      return {
        title: "Sur Firefox",
        steps: [
          <>Menu (☰) en haut à droite.</>,
          <><span className="font-bold">Installer</span> ou <span className="font-bold">Ajouter à l'écran d'accueil</span>.</>,
        ],
      };
    }
    if (browser === "safari-desktop") {
      return {
        title: "Sur Safari (Mac)",
        steps: [
          <>Menu <span className="font-bold">Fichier</span> → <span className="font-bold">Ajouter au Dock</span>.</>,
        ],
      };
    }
    // Chrome / Edge / Android fallback (si BIP pas encore tiré)
    return {
      title: "Sur ce navigateur",
      steps: [
        <>Menu (⋮ ou ☰) en haut à droite.</>,
        <><span className="font-bold">Installer l'application</span> ou <span className="font-bold">Ajouter à l'écran d'accueil</span>.</>,
      ],
    };
  })();

  return (
    <div
      className="fixed bottom-5 left-5 z-40 max-w-[300px] select-none"
      data-testid="pwa-install-floating"
    >
      {open ? (
        <div className="relative rounded-2xl shadow-2xl border border-white/15 overflow-hidden animate-in slide-in-from-bottom-3 duration-300"
             style={{ background: "linear-gradient(135deg, #4F1B7C, #2B0E4A 60%, #0a0414)" }}>
          <button
            type="button"
            onClick={dismiss}
            className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition"
            aria-label="Fermer"
            data-testid="pwa-install-dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>
          <div className="p-4 pr-9">
            <div className="flex items-center gap-2 mb-1.5">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                   style={{ background: "#EC1F7C25", color: "#EC1F7C" }}>
                <Smartphone className="w-4 h-4" />
              </div>
              <div className="font-display font-black text-white text-sm leading-tight">
                Installez l'app {clubName}
              </div>
            </div>
            <p className="text-white/70 text-[11px] leading-relaxed mb-3">
              {deferredPrompt
                ? "Accès rapide, mode hors-connexion. Installation en 1 clic."
                : "Ajoutez le raccourci sur votre écran d'accueil pour un accès rapide."}
            </p>
            {deferredPrompt ? (
              <button
                type="button"
                onClick={install}
                className="w-full inline-flex items-center justify-center gap-2 h-9 rounded-lg text-white font-black text-[12px] uppercase tracking-wider transition hover:scale-[1.02] active:scale-100"
                style={{ background: "linear-gradient(135deg, #EC1F7C, #4F1B7C)" }}
                data-testid="pwa-install-accept"
              >
                <Download className="w-3.5 h-3.5" />
                Installer l'application
              </button>
            ) : (
              <div className="text-[11px] text-white/85 bg-white/10 border border-white/15 rounded-lg p-2.5 leading-relaxed" data-testid="pwa-install-instructions">
                <div className="font-bold text-white text-[10px] uppercase tracking-wider mb-1.5">{browserInstructions.title}</div>
                <ol className="list-decimal list-inside space-y-0.5">
                  {browserInstructions.steps.map((step, i) => <li key={i}>{step}</li>)}
                </ol>
              </div>
            )}
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="w-12 h-12 rounded-full shadow-xl border border-white/20 flex items-center justify-center text-white transition hover:scale-110"
          style={{ background: "linear-gradient(135deg, #4F1B7C, #EC1F7C)" }}
          aria-label="Installer l'application sur votre appareil"
          title="Installer l'application"
          data-testid="pwa-install-fab"
        >
          <Download className="w-5 h-5" />
        </button>
      )}
    </div>
  );
}
